import { HydratedCallRecord } from '@weave/schema-gen-ts/dist/schemas/phone-exp/phone-records/v1/call_record.pb';
import { CallIntelTabType } from '@frontend/call-intel-core';
import { createShallowStore, createStoreWithSubscribe } from '@frontend/store';
import { CallInsightAccordionType, CallInsightPanelSource, ScreenType } from '../types';
import { OpenOptions, OpenParams } from './use-call-insight-controller';

export type PanelContext = {
  callListItems: OpenParams[];
  callListOptions: OpenOptions;
  defaultAccordion: CallInsightAccordionType;
  defaultCallIntelTab: CallIntelTabType;
  autoplayRecording: boolean;
  isDemoAccount?: boolean;
  nextCallItem: OpenParams | null;
  previousCallItem: OpenParams | null;
  selectedCall: HydratedCallRecord | null;
  highlightedTaskId: string | null;
  isPLGDemo?: boolean; // used to determine if current panel is in PLG demo environment, which has some differences in UI
};

// Default context for initializing a new panel source
export const defaultPanelContext: PanelContext = {
  callListItems: [],
  callListOptions: {},
  defaultAccordion: 'callIntel',
  defaultCallIntelTab: 'callSummary',
  autoplayRecording: false,
  isDemoAccount: false,
  nextCallItem: null,
  previousCallItem: null,
  selectedCall: null,
  highlightedTaskId: null,
  isPLGDemo: false,
};

type OpenContextParams = {
  call: HydratedCallRecord;
  opts?: {
    calls?: HydratedCallRecord[];
    defaultAccordion?: CallInsightAccordionType;
    defaultCallIntelTab?: CallIntelTabType;
    currentScreenView?: ScreenType;
    isDemoAccount?: boolean;
    isPLGDemo?: boolean;
    taskId?: string;
    autoplayRecording?: boolean;
  };
  source: CallInsightPanelSource;
};

type PanelContextsState = {
  activeSource: CallInsightPanelSource | null;
  contexts: Record<CallInsightPanelSource, PanelContext>;
};

// All actions for manipulating the panel store, grouped by concern
type CallInsightPanelActions = {
  closeContext: () => void;
  openContext: (params: OpenContextParams) => void;
  setDefaultAccordion: (params: { accordion: CallInsightAccordionType }) => void;
  setCallListItems: (params: {
    callListItems: OpenParams[];
    callListOptions: OpenOptions;
    source: CallInsightPanelSource;
  }) => void;
  setIsDemoAccount: (params: { isDemoAccount: boolean; source: CallInsightPanelSource }) => void;
  setIsPLGDemoInActiveSource: (isPLGDemo: boolean) => void;
  updateCallInsight: (params: { updatedCallIntelInsights?: HydratedCallRecord['callIntelInsights'] }) => void;
};

export interface CallInsightPanelStore extends PanelContextsState, CallInsightPanelActions {}

/**
 * Computes the previous and next navigation items based on the selected call.
 */
const computeCallAdjacents = (
  callListItems: OpenParams[],
  selectedCall: HydratedCallRecord | null
): { previousCallItem: OpenParams | null; nextCallItem: OpenParams | null } => {
  if (!selectedCall) return { previousCallItem: null, nextCallItem: null };

  const callRecordId = selectedCall.id;
  const channelId = selectedCall.callIntelInsights?.id;

  const selectedIndex = callListItems.findIndex((item) => {
    if ('hydratedCall' in item) return item.hydratedCall.id === callRecordId;
    if ('callRecordId' in item) return item.callRecordId === callRecordId;
    return item.channelId === channelId;
  });

  return {
    previousCallItem: selectedIndex > 0 ? callListItems[selectedIndex - 1] : null,
    nextCallItem:
      selectedIndex >= 0 && selectedIndex < callListItems.length - 1 ? callListItems[selectedIndex + 1] : null,
  };
};

// Main store implementation

export const useCallInsightPanelStore = createStoreWithSubscribe<CallInsightPanelStore>(
  (set, get) => ({
    contexts: {} as Record<CallInsightPanelSource, PanelContext>,
    activeSource: null,
    closeContext: () => {
      set((state) => {
        const source = get().activeSource;
        if (!source) return;
        const ctx = state.contexts[source];
        if (!ctx) return;
        state.contexts[source] = {
          ...ctx,
          selectedCall: null,
          previousCallItem: null,
          nextCallItem: null,
          highlightedTaskId: null,
        };
      });
    },
    openContext: ({ call, opts, source }: OpenContextParams) => {
      set((state) => {
        const prev = get().contexts?.[source] ?? defaultPanelContext;
        state.activeSource = source;
        const callListItems = prev.callListItems;
        const { previousCallItem, nextCallItem } = computeCallAdjacents(callListItems, call);
        state.contexts[source] = {
          ...prev,
          callListOptions: {
            ...prev.callListOptions,
            currentScreenView: opts?.currentScreenView,
          },
          defaultAccordion: opts?.defaultAccordion ?? prev.defaultAccordion,
          defaultCallIntelTab: opts?.defaultCallIntelTab ?? defaultPanelContext.defaultCallIntelTab,
          autoplayRecording: opts?.autoplayRecording ?? false,
          isDemoAccount: opts?.isDemoAccount ?? opts?.isPLGDemo ?? prev.isDemoAccount,
          isPLGDemo: opts?.isPLGDemo ?? prev.isPLGDemo,
          selectedCall: call,
          previousCallItem,
          nextCallItem,
          highlightedTaskId: opts?.taskId ?? null,
        };
      });
    },
    setDefaultAccordion: ({ accordion }) => {
      set((state) => {
        const source = get().activeSource;
        if (!source) return;
        const ctx = state.contexts[source];
        if (!ctx) return;
        state.contexts[source] = { ...ctx, defaultAccordion: accordion };
      });
    },
    setCallListItems: ({ callListItems, callListOptions, source }) => {
      set((state) => {
        const ctx = state.contexts[source] ?? defaultPanelContext;
        const { selectedCall } = ctx;
        const { previousCallItem, nextCallItem } = computeCallAdjacents(callListItems, selectedCall);
        state.contexts[source] = { ...ctx, callListItems, callListOptions, previousCallItem, nextCallItem };
      });
    },
    setIsDemoAccount: ({ isDemoAccount, source }) => {
      set((state) => {
        const ctx = state.contexts[source] ?? defaultPanelContext;
        state.contexts[source] = { ...ctx, isDemoAccount };
      });
    },
    setIsPLGDemoInActiveSource: (isPLGDemo) => {
      set((state) => {
        const source = get().activeSource;
        if (!source) return;
        const ctx = state.contexts[source] ?? defaultPanelContext;
        state.contexts[source] = { ...ctx, isPLGDemo, isDemoAccount: isPLGDemo };
      });
    },
    updateCallInsight: ({ updatedCallIntelInsights }) => {
      set((state) => {
        const source = get().activeSource;
        if (!source) return;
        const ctx = state.contexts[source];
        if (!ctx || !ctx.selectedCall) return;
        state.contexts[source] = {
          ...ctx,
          selectedCall: {
            ...ctx.selectedCall,
            callIntelInsights: updatedCallIntelInsights ?? ctx.selectedCall.callIntelInsights,
          },
        };
      });
    },
  }),
  {
    name: 'CallInsightPanelStore',
    trace: true,
  }
);

export const useCallInsightPanelShallowStore = createShallowStore(useCallInsightPanelStore);
